import type { Agent, AgentAction } from "./fixtures";
import {
  StatusDot,
  AutonomyBadge,
  TriggerBadge,
  ReversibilityTag,
} from "./badges";
import { AgentMenu } from "./agent-menu";

/**
 * A routine (agent) card that expands INLINE in place — the post-pivot replacement for the
 * old chip-strip + lens-tabs detail. Collapsed: identity + autonomy/trigger badges + footer.
 * Expanded (accordion, single-open, controlled by the parent): the consolidated view —
 * HOW IT WORKS (read-only config, refine via chat) │ RECENT ACTIVITY (short run list →
 * Activity), with the pending action (Confirm) pinned top-right and a ^ to collapse.
 */
export function RoutineCard({
  agent,
  expanded,
  onToggle,
  onRefineInChat,
  onOpenActivity,
  onDeleted,
}: {
  agent: Agent;
  expanded: boolean;
  onToggle: () => void;
  /** Scope the chat to this agent and surface the input (design §3). */
  onRefineInChat: () => void;
  /** Open the Activity tab filtered to this agent. */
  onOpenActivity: () => void;
  /** The agent was deleted from the ⋯ menu — clear any selection/scope. */
  onDeleted: () => void;
}) {
  const on = agent.status === "active";
  return (
    <div
      className={`agui-routine${expanded ? " is-expanded" : ""}${on ? "" : " is-paused"}`}
    >
      <div
        className="agui-routine__head"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="agui-routine__id">
          <div className="agui-routine__name">
            <StatusDot color={agent.color} status={agent.status} />
            {agent.name}
          </div>
          <div className="agui-routine__role">{agent.role}</div>
        </div>
        <div
          className="agui-routine__headright"
          onClick={(e) => e.stopPropagation()}
        >
          {expanded && agent.pendingCount > 0 ? (
            <span className="agui-confirmpill">Confirm</span>
          ) : null}
          {expanded ? (
            <button
              type="button"
              className="agui-routine__chev"
              aria-label="Collapse"
              onClick={onToggle}
            >
              ⌃
            </button>
          ) : (
            <AgentMenu agent={agent} onDeleted={onDeleted} />
          )}
        </div>
      </div>

      {expanded ? (
        <RoutineExpanded
          agent={agent}
          onRefineInChat={onRefineInChat}
          onOpenActivity={onOpenActivity}
        />
      ) : (
        <>
          <div className="agui-routine__meta">
            <AutonomyBadge level={agent.autonomy} />
            <TriggerBadge mode={agent.triggerMode} />
            {agent.pendingCount > 0 ? (
              <span className="agui-pendingpill">
                {agent.pendingCount} to sign off
              </span>
            ) : null}
          </div>
          <div className="agui-routine__foot">
            <span className="agui-routine__activity">
              {agent.recentActivity}
            </span>
            {agent.nextRunLabel ? (
              <span className="agui-routine__run">
                Next {agent.nextRunLabel}
              </span>
            ) : agent.lastRunLabel ? (
              <span className="agui-routine__run">
                Last {agent.lastRunLabel}
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/** The expanded body: HOW IT WORKS (config, read-only) │ RECENT ACTIVITY (runs). */
function RoutineExpanded({
  agent,
  onRefineInChat,
  onOpenActivity,
}: {
  agent: Agent;
  onRefineInChat: () => void;
  onOpenActivity: () => void;
}) {
  const b = agent.build;
  return (
    <div className="agui-routine__body">
      <section className="agui-routine__col">
        <div className="agui-block__label">How it works</div>
        <div className="agui-built">
          <div className="agui-built__row">
            <span className="agui-built__k">Trigger</span>
            <span>
              {b.trigger}
              {b.triggerToken ? (
                <>
                  {" "}
                  <span className="agui-token">{b.triggerToken}</span>
                </>
              ) : null}
            </span>
          </div>
          {b.condition ? (
            <div className="agui-built__row">
              <span className="agui-built__k">Cond.</span>
              <span>{b.condition}</span>
            </div>
          ) : null}
          <div className="agui-built__row">
            <span className="agui-built__k">Actions</span>
            <span className="agui-actiontags">
              {b.actions.map((a) => (
                <ReversibilityTag
                  key={a.id}
                  kind={a.reversibility}
                  label={`${terse(a)} · ${a.reversibility}`}
                />
              ))}
            </span>
          </div>
        </div>
        <button type="button" className="agui-link" onClick={onRefineInChat}>
          Refine by talking in chat →
        </button>
      </section>

      <section className="agui-routine__col agui-routine__col--activity">
        <div className="agui-block__label">Recent activity</div>
        <ul className="agui-runs">
          {agent.runs.map((r) => (
            <li key={r.id} className="agui-run">
              <span className="agui-run__when">{r.when}</span>
              <span className="agui-run__sum">· {r.summary}</span>
              <span
                className={`agui-run__status agui-run__status--${r.status}`}
              >
                {r.status === "auto" && r.trusted ? "auto · trusted" : r.status}
              </span>
            </li>
          ))}
        </ul>
        <button type="button" className="agui-link" onClick={onOpenActivity}>
          Open in Activity →
        </button>
      </section>
    </div>
  );
}

/** Terse action verb ("draft", "send", "read", …) derived from the action label. */
function terse(a: AgentAction): string {
  const l = a.label.toLowerCase();
  if (l.includes("draft")) return "draft";
  if (l.includes("send")) return "send";
  if (l.includes("label")) return "label";
  if (l.includes("write") || l.includes("post")) return "write";
  if (
    l.includes("read") ||
    l.includes("gather") ||
    l.includes("pull") ||
    l.includes("classif")
  )
    return "read";
  return l.split(" ")[0];
}
